import React, { useState, useEffect } from 'react';
import { TextField, Button, Box, FormControlLabel, Checkbox, Alert } from '@mui/material';
import { User } from '../../models/user';

interface LoginFormProps {
  onLogin: (user: User) => void;
  savedEmail?: string;
  onRememberMeChange: (remember: boolean) => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onLogin, savedEmail = '', onRememberMeChange }) => {
  const [email, setEmail] = useState(savedEmail);
  const [password, setPassword] = useState('');
  const [rememberMe] = useState(!!savedEmail);
  const [isKioskDevice, setIsKioskDevice] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [userWantsToStay, setUserWantsToStay] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (navigator.platform !== 'Linux x86_64') return;

    const checkForKioskDevice = async () => {
      try {
        const response = await fetch('http://127.0.0.1:3000/msg?getDeviceInfo', {
          method: 'GET',
          signal: AbortSignal.timeout(2000)
        });

        if (response.ok) {
          setIsKioskDevice(true);
        }
      } catch (error) {
        // Silently fail - kiosk app not running
      }
    };

    checkForKioskDevice();
  }, []);

  useEffect(() => {
    if (!isKioskDevice || userWantsToStay) return;

    if (countdown === 0) {
      console.log('Raspberry Pi kiosk detected - restarting browser...');
      fetch('http://127.0.0.1:3000/msg?restartBrowser', {
        method: 'GET',
        signal: AbortSignal.timeout(2000)
      });
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [isKioskDevice, countdown, userWantsToStay]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const user: User = await response.json();
        onLogin(user);
      } else if (response.status === 401) {
        // Same message for unknown email and wrong password: don't reveal which accounts exist.
        setError('Invalid email or password.');
      } else {
        setError(`Login failed (server error ${response.status}). Please try again.`);
      }
    } catch (error) {
      console.error('Error during login:', error);
      setError('Cannot reach the server. Check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      component="form"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onSubmit={handleSubmit}
    >
      {isKioskDevice && !userWantsToStay && (
        <Box
          sx={{
            width: '100%',
            mb: 2,
            p: 2,
            bgcolor: 'warning.main',
            color: 'warning.contrastText',
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Box sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
            Redirecting to dashboard in {countdown} second{countdown !== 1 ? 's' : ''}...
          </Box>
          <Button
            variant="contained"
            color="inherit"
            size="small"
            onClick={() => setUserWantsToStay(true)}
          >
            Click Here to Stay on Login Page
          </Button>
        </Box>
      )}
      <TextField
        label="Email"
        variant="outlined"
        margin="normal"
        required
        fullWidth
        value={email}
        onChange={(e) => { setEmail(e.target.value); setError(null); }}
        autoComplete="email"
        error={!!error}
      />
      <TextField
        label="Password"
        variant="outlined"
        margin="normal"
        required
        fullWidth
        type="password"
        value={password}
        onChange={(e) => { setPassword(e.target.value); setError(null); }}
        autoComplete="current-password"
        error={!!error}
      />
      {error && (
        <Alert severity="error" sx={{ width: '100%', mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <FormControlLabel
        control={
          <Checkbox
            onChange={(e) => onRememberMeChange(e.target.checked)}
            defaultChecked={rememberMe}
            color="primary"
          />
        }
        label="Remember Me"
        sx={{ alignSelf: 'flex-start', mt: 1 }}
      />
      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        size="large"
        disabled={submitting}
        sx={{ mt: 3, py: 1.5 }}
      >
        {submitting ? 'Logging in...' : 'Login'}
      </Button>
    </Box >
  );
};

export default LoginForm;